import { SIMULATION_STEP_MS } from "../shared/constants";
import { addOrReconnectPlayer, applyInput, createRoomState, stepRoom, toSnapshot, tryStartMatch } from "../shared/simulation";
import type { PlayerInput } from "../shared/types";

export type PracticeStep = "move" | "turn" | "depth" | "sonar" | "fire" | "complete";

export const practiceInstructions: Record<PracticeStep, { title: string; hint: string }> = {
  move: { title: "1. Move 3 metres", hint: "Hold W or Forward. Your submarine accelerates and coasts when you release." },
  turn: { title: "2. Change heading", hint: "Hold A / D or Left / Right to turn a little. Torpedoes follow your heading." },
  depth: { title: "3. Dive 3 metres", hint: "Hold S or Dive. F or Rise brings you back up. Watch your depth below the surface." },
  sonar: { title: "4. Read sonar", hint: "Sonar is always on. Find Practice target and its distance, then confirm the contact below." },
  fire: { title: "5. Hit the target", hint: "Firing lane reset: the target is straight ahead at your depth. Press Space or Fire. If you move off course, reset the lane." },
  complete: { title: "Ready for a friends match", hint: "You moved, turned, changed depth, read sonar and landed a torpedo. Create a room and share its invite link." }
};

export class PracticeSession {
  readonly room = createRoomState("PRACTICE", 0, { config: { durationMs: 20 * 60_000 } });
  readonly captain = addOrReconnectPlayer(this.room, { id: "practice-captain", token: "local-captain", name: "You", now: 0 });
  readonly target = addOrReconnectPlayer(this.room, { id: "practice-target", token: "local-target", name: "Practice target", now: 0 });
  step: PracticeStep = "move";
  feedback = "Try these controls at your own pace. You can leave or retry at any time.";
  private time = 4000;
  private origin = { ...this.captain.position };
  private heading = this.captain.rotation;

  constructor() {
    this.captain.ready = this.target.ready = true;
    tryStartMatch(this.room, 0);
    this.room.endsAt = null;
    this.origin = { ...this.captain.position };
    this.heading = this.captain.rotation;
    this.target.invulnerableUntil = 0;
  }

  tick(input: PlayerInput): void {
    if (this.step === "complete") return;
    this.time += SIMULATION_STEP_MS;
    applyInput(this.room, this.captain.id, { ...input, fire: this.step === "fire" && input.fire, homing: false, mine: false });
    const events = stepRoom(this.room, this.time);
    if (this.step === "move" && Math.hypot(this.captain.position.x - this.origin.x, this.captain.position.z - this.origin.z) >= 3) {
      this.advance("turn", "Moved 3 metres. Release Forward and try steering.");
    } else if (this.step === "turn" && this.headingChange() >= 0.35) {
      this.origin = { ...this.captain.position };
      this.advance("depth", "Heading changed. Now try diving.");
    } else if (this.step === "depth" && this.origin.y - this.captain.position.y >= 3) {
      this.advance("sonar", "Dived 3 metres. Sonar shows contacts even when they are hard to see.");
    } else if (this.step === "fire" && events.some(event => event.type === "player_damaged" && event.playerId === this.captain.id && event.targetId === this.target.id && event.source === "regular")) {
      this.advance("complete", "Torpedo hit confirmed by the combat simulation.");
    }
  }

  confirmContact(): void {
    if (this.step !== "sonar") return;
    this.resetLane();
    this.advance("fire", "Contact identified. Match the target's depth before firing in a real match.");
  }

  resetLane(): void {
    this.captain.position = { x: 0, y: -24, z: 0 };
    this.captain.velocity = { x: 0, y: 0, z: 0 };
    this.captain.rotation = 0;
    this.target.position = { x: -18, y: -24, z: 0 };
    this.target.velocity = { x: 0, y: 0, z: 0 };
    this.target.health = 100;
    this.target.alive = true;
    this.target.invulnerableUntil = 0;
    this.room.projectiles = [];
    this.room.mines = [];
  }

  snapshot() {
    return toSnapshot(this.room, [], this.time);
  }

  progress(): string {
    if (this.step === "move") return `${Math.min(3, Math.hypot(this.captain.position.x - this.origin.x, this.captain.position.z - this.origin.z)).toFixed(1)} / 3m travelled`;
    if (this.step === "turn") return `${Math.min(20, Math.round(this.headingChange() * 180 / Math.PI))} / 20 degrees turned`;
    if (this.step === "depth") return `${Math.min(3, Math.max(0, this.origin.y - this.captain.position.y)).toFixed(1)} / 3m deeper`;
    if (this.step === "fire") return "Waiting for a regular torpedo hit";
    return this.step === "complete" ? "5 / 5 basics tried" : "Find the target in the sonar readout";
  }

  private headingChange(): number {
    const change = this.captain.rotation - this.heading;
    return Math.abs(Math.atan2(Math.sin(change), Math.cos(change)));
  }

  private advance(step: PracticeStep, feedback: string): void {
    this.step = step;
    this.feedback = feedback;
  }
}
